/**
 * ProductDetail - Single product page with customization options
 * Includes detailed product info tabs with fun descriptions
 */
import React, { useState, useMemo, useEffect } from 'react';
import { FONT_FAMILY, SHOP_SPACING } from '../ShopStyles';
import { getProductById, getProductImageForVariant } from '../data/products';
import PetPortraitCreator from '../../components/PetPortraitCreator';
import StarRating from '../components/StarRating';

// Fun taglines per product category
const funTaglines = {
  mugs: "☕ The only mug that'll make your morning meetings bearable",
  apparel: "👕 Warning: May cause excessive compliments from strangers",
  canvas: "🖼️ Museum-quality art, featuring the cutest curator",
  phone: "📱 Finally, a case as cute as your lock screen wallpaper",
  default: "✨ Because your pet deserves to be famous",
};

// Fun descriptions per category
const funDescriptions = {
  mugs: {
    tagline: "Your pet's face + your morning coffee = the perfect start to any day! ☕",
    vibe: "Imagine sipping your latte while your fur baby stares lovingly back at you. It's basically a hug in mug form.",
    funFact: "Fun fact: Studies show that looking at cute animals reduces stress. So technically, this mug is a wellness product. 🧘‍♀️",
  },
  apparel: {
    tagline: "Turn heads and melt hearts wherever you go! 👕",
    vibe: "Be the person at the party everyone wants to talk to (spoiler: they want to talk about your shirt... and your pet).",
    funFact: "Fun fact: Wearing your pet's portrait increases your daily 'awww' count by approximately 47%. Science! 🔬",
  },
  canvas: {
    tagline: "Your pet, but make it ~fancy~ 🎨",
    vibe: "Every home deserves a masterpiece. Your pet is that masterpiece. We just put it on canvas.",
    funFact: "Fun fact: Hanging pet portraits has been shown to make your walls 1000% more interesting. That's a real statistic we made up. 📊",
  },
  phone: {
    tagline: "Protection + cuteness = the ultimate combo 📱",
    vibe: "Your phone goes everywhere with you. Shouldn't your pet? Now they can!",
    funFact: "Fun fact: People with pet phone cases are scientifically proven to be cooler. We're definitely scientists. 🧪",
  },
  default: {
    tagline: "Your pet, transformed into pure joy! ✨",
    vibe: "Because some things are just too cute to keep to yourself.",
    funFact: "Fun fact: We've never met a pet we couldn't make look absolutely amazing. Your pet is no exception! 🌟",
  },
};

const ProductDetail = ({ 
  productId, 
  onNavigate, 
  onAddToCart,
  generatedImage: existingImage,
  petName: existingPetName,
  onGeneratePortrait,
}) => {
  const product = useMemo(() => getProductById(productId), [productId]);
  
  const [selectedVariant, setSelectedVariant] = useState(product?.variants[0]?.id || '');
  const [selectedSize, setSelectedSize] = useState(product?.sizes[0] || '');
  const [quantity, setQuantity] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('about');
  
  // Portrait state - sync with props to persist across products
  const [showPortraitModal, setShowPortraitModal] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(existingImage);
  const [petName, setPetName] = useState(existingPetName || '');

  // Sync portrait state when navigating between products
  useEffect(() => {
    if (existingImage) {
      setGeneratedImage(existingImage);
    }
    if (existingPetName) {
      setPetName(existingPetName);
    }
  }, [existingImage, existingPetName, productId]);

  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'product-detail-animations';
    styleSheet.textContent = `
      @keyframes floatPaw {
        0%, 100% { transform: translateY(0) rotate(-5deg); }
        50% { transform: translateY(-6px) rotate(5deg); }
      }
      @keyframes sparkle {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.7; transform: scale(1.2); }
      }
      @keyframes wiggle {
        0%, 100% { transform: rotate(-2deg); }
        50% { transform: rotate(2deg); }
      }
      .option-pill:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(147, 51, 234, 0.2) !important;
      }
      /* Fun button hover effects */
      .fun-button {
        transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
      }
      .fun-button:hover {
        transform: translateY(-3px) scale(1.03) !important;
        box-shadow: 0 8px 25px rgba(147, 51, 234, 0.4) !important;
      }
      .fun-button:active {
        transform: translateY(-1px) scale(1.01) !important;
      }
    `;
    document.head.appendChild(styleSheet);
    return () => {
      const existing = document.getElementById('product-detail-animations');
      if (existing) existing.remove();
    };
  }, []);

  const handlePortraitComplete = (result) => {
    setGeneratedImage(result.image);
    setPetName(result.petName);
    setShowPortraitModal(false);
    onGeneratePortrait?.(result.image, result.petName);
  };

  if (!product) {
    return (
      <div style={styles.container}>
        <div style={styles.errorState}>
          <span style={styles.errorIcon}>😿</span>
          <p style={styles.errorText}>Product not found</p>
          <button onClick={() => onNavigate?.('products')} style={styles.backButtonError}>
            ← Back to Products
          </button>
        </div>
      </div>
    );
  }

  // Get category-specific fun content
  const category = product.category || 'default';
  const funContent = funDescriptions[category] || funDescriptions.default;
  const tagline = funTaglines[category] || funTaglines.default;

  // Product info data
  const productInfo = {
    technique: 'Sublimation',
    techniqueDesc: 'We use space-age heat transfer technology (okay, it\'s just really good printing)',
    material: category === 'mugs' ? 'Ceramic' : category === 'apparel' ? 'Cotton blend' : 'Premium quality',
    materialDesc: category === 'mugs' ? 'Tough enough to survive Monday mornings' : 'Soft enough to nap in (we won\'t judge)',
    features: category === 'mugs' ? 'Glossy finish' : 'Vibrant colors',
    featuresDesc: category === 'mugs' ? 'So shiny you can almost see your own cute face' : 'Colors so vivid, your pet might not recognize themselves',
    sizes: product.sizes.join(', '),
    shipping: '6-9 business days',
    shippingCost: '$6.49',
  };

  const selectedVariantData = product.variants.find(v => v.id === selectedVariant);
  
  // Get the variant-specific image
  const variantImage = getProductImageForVariant(product, selectedVariant);
  
  const galleryImages = [
    { id: 'main', url: variantImage },
    { id: 'detail', url: variantImage },
    { id: 'lifestyle', url: variantImage },
  ];
  const currentImage = generatedImage || existingImage;

  const handleAddToCart = () => {
    onAddToCart?.({
      id: product.id,
      variantId: selectedVariant,
      name: product.name,
      variant: selectedVariantData?.name,
      size: selectedSize,
      price: product.price,
      quantity,
      image: variantImage, // Use variant-specific image
      petImage: currentImage,
      petName: petName || existingPetName,
    });
  };

  const quantityOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  const tabs = [
    { id: 'about', label: '✨ The Vibe', emoji: '✨' },
    { id: 'details', label: '📋 The Details', emoji: '📋' },
    { id: 'shipping', label: '📦 Shipping', emoji: '📦' },
  ];

  return (
    <div style={styles.container}>
      {/* Minimal back link - progress bar handles main navigation */}
      <button 
        onClick={() => onNavigate?.('products')} 
        style={styles.backLink}
        onMouseEnter={(e) => e.currentTarget.style.color = '#7c3aed'}
        onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
      >
        ← All Products
      </button>

      <div style={styles.productLayout}>
        {/* Left: Gallery */}
        <div style={styles.gallerySection}>
          <div style={styles.mainImageWrapper}>
            <img
              src={galleryImages[selectedImageIndex].url}
              alt={product.name}
              style={styles.mainImage}
            />
            
            {currentImage && (
              <div style={styles.petBadge}>
                <img src={currentImage} alt={petName || existingPetName} style={styles.petBadgeImage} />
                <span>{petName || existingPetName}'s Art!</span>
              </div>
            )}
            
            {product.popular && (
              <div style={styles.popularBadge}>⭐ Popular</div>
            )}
          </div>
          
          <div style={styles.thumbnailRow}>
            {galleryImages.map((img, idx) => (
              <button
                key={img.id}
                onClick={() => setSelectedImageIndex(idx)}
                style={{
                  ...styles.thumbnail,
                  borderColor: selectedImageIndex === idx ? '#9333ea' : '#e5e7eb',
                }}
              >
                <img src={img.url} alt={`View ${idx + 1}`} style={styles.thumbnailImage} />
              </button>
            ))}
          </div>
        </div>

        {/* Right: Product Info */}
        <div style={styles.infoSection}>
          <h1 style={styles.productName}>{product.name}</h1>
          
          {/* Star Rating */}
          {product.rating && (
            <div style={{ marginBottom: '12px' }}>
              <StarRating 
                rating={product.rating} 
                reviewCount={product.reviewCount}
                size="medium"
              />
            </div>
          )}
          
          {/* Fun Tagline */}
          <p style={styles.funTagline}>{tagline}</p>
          
          {/* Social Proof & Urgency Indicators */}
          <div style={styles.socialProof}>
            <div style={styles.proofBadge}>
              <span style={styles.proofIcon}>👀</span>
              <span style={styles.proofText}>{Math.floor(Math.random() * 25) + 15} people viewing now</span>
            </div>
            <div style={styles.proofBadge}>
              <span style={styles.proofIcon}>🔥</span>
              <span style={styles.proofText}>{product.reviewCount || 50}+ sold this week</span>
            </div>
          </div>
          
          {/* Price & Delivery */}
          <div style={styles.priceDeliveryRow}>
            <div style={styles.priceBox}>
              <span style={styles.priceLabel}>Price</span>
              <span style={styles.price}>${product.price.toFixed(2)}</span>
              <span style={styles.priceNote}>Print included</span>
            </div>
            <div style={styles.deliveryBox}>
              <span style={styles.deliveryLabel}>Estimated delivery</span>
              <span style={styles.deliveryFlag}>🇺🇸 United States</span>
              <span style={styles.deliveryTime}>Jan 15–17</span>
              <span style={styles.shippingNote}>Shipping starts at: {productInfo.shippingCost}</span>
            </div>
          </div>

          {/* Options Card */}
          <div style={styles.optionsCard}>
            {/* Color */}
            {product.variants.length > 1 && (
              <div style={styles.optionGroup}>
                <label style={styles.optionLabel}>Color</label>
                <div style={styles.pillRow}>
                  {product.variants.map((variant) => {
                    const isWhite = variant.color === '#ffffff' || variant.color === 'white' || variant.color === '#fff';
                    const isSelected = selectedVariant === variant.id;
                    return (
                      <button
                        key={variant.id}
                        className="option-pill"
                        onClick={() => setSelectedVariant(variant.id)}
                        style={{
                          ...styles.optionPill,
                          borderColor: isSelected ? '#9333ea' : '#e5e7eb',
                          background: isSelected ? 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)' : '#fff',
                          color: isSelected ? '#fff' : '#374151',
                        }}
                      >
                        <div
                          style={{
                            ...styles.colorDot,
                            background: variant.color === 'transparent' 
                              ? 'linear-gradient(135deg, #fff 0%, #ddd 100%)' 
                              : variant.color,
                            border: isWhite 
                              ? '2px solid #d1d5db' 
                              : isSelected 
                                ? '2px solid rgba(255,255,255,0.5)' 
                                : 'none',
                          }}
                        />
                        {variant.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Size */}
            {product.sizes.length > 1 && (
              <div style={styles.optionGroup}>
                <label style={styles.optionLabel}>Size</label>
                <div style={styles.pillRow}>
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      className="option-pill"
                      onClick={() => setSelectedSize(size)}
                      style={{
                        ...styles.optionPill,
                        ...styles.sizePill,
                        borderColor: selectedSize === size ? '#9333ea' : '#e5e7eb',
                        background: selectedSize === size ? 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)' : '#fff',
                        color: selectedSize === size ? '#fff' : '#374151',
                      }}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity - Compact Dropdown */}
            <div style={styles.optionGroup}>
              <label style={styles.optionLabel}>Quantity</label>
              <select
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
                style={styles.qtySelect}
              >
                {quantityOptions.map(num => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Pet Portrait Section - With fun description */}
          {!currentImage ? (
            <div style={styles.portraitPrompt}>
              <div style={styles.promptHeader}>
                <div style={styles.promptIconCircle}>
                  <span style={styles.promptIcon}>🐾</span>
                </div>
                <div>
                  <h3 style={styles.promptTitle}>Add Your Pet's Portrait!</h3>
                  <p style={styles.promptSubtitle}>This is where the magic happens ✨</p>
                </div>
              </div>
              
              <div style={styles.promptDescription}>
                <p style={styles.promptText}>
                  Upload a pic of your furry (or scaly, or feathery) friend, pick an awesome style, 
                  and watch our AI create a one-of-a-kind masterpiece! 🎨
                </p>
                <div style={styles.promptSteps}>
                  <span style={styles.promptStep}>📸 Upload photo</span>
                  <span style={styles.promptArrow}>→</span>
                  <span style={styles.promptStep}>🎨 Pick style</span>
                  <span style={styles.promptArrow}>→</span>
                  <span style={styles.promptStep}>✨ Magic!</span>
                </div>
              </div>
              
              <button onClick={() => setShowPortraitModal(true)} style={styles.createPortraitBtn}>
                <span>📸</span> Create Pet Portrait <span>→</span>
              </button>
              
              <p style={styles.promptNote}>Takes about 30 seconds. Your pet's fame awaits! 🌟</p>
            </div>
          ) : (
            <div style={styles.portraitReadySection}>
              <div style={styles.portraitReady}>
                <img src={currentImage} alt={petName || existingPetName} style={styles.readyThumb} />
                <div style={styles.readyInfo}>
                  <span style={styles.readyCheck}>✓ Portrait Ready!</span>
                  <span style={styles.readyName}>{petName || existingPetName}</span>
                </div>
                <button 
                  onClick={() => {
                    setGeneratedImage(null);
                    setShowPortraitModal(true);
                  }} 
                  style={styles.changeLink}
                >
                  Change
                </button>
              </div>
              
              {/* See on All Products CTA */}
              <button 
                onClick={() => onNavigate?.('products')}
                style={styles.seeAllProductsBtn}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(147, 51, 234, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 12px rgba(147, 51, 234, 0.15)';
                }}
              >
                <span style={styles.seeAllIcon}>✨</span>
                <span>See {petName || existingPetName} on all products</span>
                <span style={styles.seeAllArrow}>→</span>
              </button>
            </div>
          )}

          {/* Add to Cart */}
          <button
            onClick={handleAddToCart}
            disabled={!currentImage}
            className="fun-button"
            style={{
              ...styles.addToCartBtn,
              opacity: currentImage ? 1 : 0.6,
              cursor: currentImage ? 'pointer' : 'not-allowed',
            }}
          >
            🛒 Add to Cart — ${(product.price * quantity).toFixed(2)}
          </button>

          {!currentImage && (
            <p style={styles.cartHint}>👆 Create your pet portrait first</p>
          )}
          
          {/* Trust Signals */}
          <div style={styles.trustSignals}>
            <div style={styles.trustItem}>
              <span style={styles.trustIcon}>✓</span>
              <span style={styles.trustText}>100% Satisfaction Guarantee</span>
            </div>
            <div style={styles.trustItem}>
              <span style={styles.trustIcon}>🔒</span>
              <span style={styles.trustText}>Secure Checkout</span>
            </div>
            <div style={styles.trustItem}>
              <span style={styles.trustIcon}>🚚</span>
              <span style={styles.trustText}>Free shipping over $50</span>
            </div>
          </div>
        </div>
      </div>

      {/* Product Information Tabs - Segmented control style */}
      <div style={styles.tabsSection}>
        {/* Tab Bar */}
        <div style={styles.tabBarWrapper}>
          <div style={styles.tabBar}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  ...styles.tabBtn,
                  ...(activeTab === tab.id ? styles.tabBtnActive : {}),
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content Panel - Consistent box */}
        <div style={styles.tabPanel}>
          {activeTab === 'about' && (
            <div style={styles.vibeContent}>
              <div style={styles.vibeCard}>
                <span style={styles.vibeEmoji}>💖</span>
                <h3 style={styles.vibeTitle}>{funContent.tagline}</h3>
                <p style={styles.vibeText}>{funContent.vibe}</p>
                <div style={styles.funFactBox}>
                  <span style={styles.funFactEmoji}>🤓</span>
                  <p style={styles.funFactText}>{funContent.funFact}</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'details' && (
            <div style={styles.detailsGrid}>
              <div style={styles.detailCard}>
                <span style={styles.detailEmoji}>🖨️</span>
                <h4 style={styles.detailTitle}>How We Make It</h4>
                <p style={styles.detailValue}>{productInfo.technique}</p>
                <p style={styles.detailDesc}>{productInfo.techniqueDesc}</p>
              </div>
              <div style={styles.detailCard}>
                <span style={styles.detailEmoji}>🧱</span>
                <h4 style={styles.detailTitle}>What It's Made Of</h4>
                <p style={styles.detailValue}>{productInfo.material}</p>
                <p style={styles.detailDesc}>{productInfo.materialDesc}</p>
              </div>
              <div style={styles.detailCard}>
                <span style={styles.detailEmoji}>✨</span>
                <h4 style={styles.detailTitle}>The Cool Part</h4>
                <p style={styles.detailValue}>{productInfo.features}</p>
                <p style={styles.detailDesc}>{productInfo.featuresDesc}</p>
              </div>
              <div style={styles.detailCard}>
                <span style={styles.detailEmoji}>📐</span>
                <h4 style={styles.detailTitle}>Sizes</h4>
                <p style={styles.detailValue}>{productInfo.sizes}</p>
                <p style={styles.detailDesc}>Something for everyone!</p>
              </div>
            </div>
          )}

          {activeTab === 'shipping' && (
            <div style={styles.shippingContent}>
              <div style={styles.shippingCard}>
                <div style={styles.shippingIcon}>🚀</div>
                <h3 style={styles.shippingTitle}>We Ship Fast!</h3>
                <p style={styles.shippingSubtitle}>Your pet's fame is on its way</p>
                
                <div style={styles.shippingTable}>
                  <div style={styles.shippingRow}>
                    <span>📍 Destination</span>
                    <span style={styles.shippingValue}>🇺🇸 United States</span>
                  </div>
                  <div style={styles.shippingRow}>
                    <span>⏱️ Delivery Time</span>
                    <span style={styles.shippingValue}>{productInfo.shipping}</span>
                  </div>
                  <div style={styles.shippingRow}>
                    <span>💰 First Item</span>
                    <span style={styles.shippingValue}>{productInfo.shippingCost}</span>
                  </div>
                  <div style={styles.shippingRow}>
                    <span>➕ Each Additional</span>
                    <span style={styles.shippingValue}>$3.50</span>
                  </div>
                </div>
                
                <p style={styles.shippingNote}>
                  * Made fresh for you! Actual delivery times may vary based on demand. 
                  Worth the wait, we promise! 💜
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Portrait Creator Modal */}
      {showPortraitModal && (
        <PetPortraitCreator
          isModal={true}
          isWide={true}
          onClose={() => setShowPortraitModal(false)}
          onComplete={handlePortraitComplete}
          initialPetName={petName}
          productName={product.name}
        />
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: `${SHOP_SPACING.md} 0`,
  },
  // Minimal back link
  backLink: {
    background: 'none',
    border: 'none',
    color: '#9ca3af',
    fontSize: '14px',
    fontFamily: FONT_FAMILY,
    fontWeight: '500',
    cursor: 'pointer',
    padding: '8px 0',
    marginBottom: '12px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    transition: 'color 0.2s ease',
  },
  // Deprecated - replaced by breadcrumbs
  headerBar: {
    display: 'none',
  },
  backNav: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: '#fff',
    border: '2px solid #9333ea',
    borderRadius: '10px',
    color: '#9333ea',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: FONT_FAMILY,
    cursor: 'pointer',
    padding: '10px 18px',
    boxShadow: '0 2px 8px rgba(147, 51, 234, 0.15)',
    transition: 'all 0.2s ease',
  },
  productLayout: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '48px',
    alignItems: 'start',
  },
  
  // Gallery
  gallerySection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  mainImageWrapper: {
    position: 'relative',
    borderRadius: '20px',
    overflow: 'hidden',
    background: '#f5f5f5',
    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
  },
  mainImage: {
    width: '100%',
    display: 'block',
  },
  thumbnailRow: {
    display: 'flex',
    gap: '12px',
  },
  thumbnail: {
    width: '80px',
    height: '80px',
    borderRadius: '12px',
    overflow: 'hidden',
    border: '3px solid',
    cursor: 'pointer',
    flexShrink: 0,
    padding: 0,
    outline: 'none',
    flexShrink: 0,
  },
  thumbnailImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  petBadge: {
    position: 'absolute',
    bottom: '12px',
    right: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'rgba(255,255,255,0.95)',
    padding: '6px 12px 6px 6px',
    borderRadius: '50px',
    fontSize: '12px',
    fontWeight: '600',
    color: '#7c3aed',
    fontFamily: FONT_FAMILY,
    boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
  },
  petBadgeImage: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '2px solid #9333ea',
  },
  popularBadge: {
    position: 'absolute',
    top: '12px',
    left: '12px',
    background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
    color: '#fff',
    fontSize: '12px',
    fontWeight: '700',
    padding: '6px 12px',
    borderRadius: '50px',
    fontFamily: FONT_FAMILY,
  },
  
  // Info Section
  infoSection: {},
  productName: {
    fontSize: '32px',
    fontWeight: '800',
    fontFamily: FONT_FAMILY,
    color: '#1f2937',
    margin: 0,
    marginBottom: '10px',
    letterSpacing: '-0.5px',
  },
  funTagline: {
    fontSize: '17px',
    color: '#7c3aed',
    fontFamily: FONT_FAMILY,
    fontWeight: '500',
    margin: 0,
    marginBottom: '16px',
  },
  socialProof: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '24px',
  },
  proofBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    background: '#fef3c7',
    borderRadius: '20px',
    border: '1px solid #fcd34d',
  },
  proofIcon: {
    fontSize: '14px',
  },
  proofText: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#92400e',
    fontFamily: FONT_FAMILY,
  },
  
  // Price & Delivery Row
  priceDeliveryRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '20px',
  },
  priceBox: {
    background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
    borderRadius: '14px',
    padding: '18px',
    display: 'flex',
    flexDirection: 'column',
    border: '2px solid #e9d5ff',
  },
  priceLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#6b7280',
    fontFamily: FONT_FAMILY,
    marginBottom: '4px',
  },
  price: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
  },
  priceNote: {
    fontSize: '12px',
    color: '#9ca3af',
    fontFamily: FONT_FAMILY,
  },
  deliveryBox: {
    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    borderRadius: '14px',
    padding: '18px',
    display: 'flex',
    flexDirection: 'column',
    border: '2px solid #bbf7d0',
  },
  deliveryLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#6b7280',
    fontFamily: FONT_FAMILY,
    marginBottom: '4px',
  },
  deliveryFlag: {
    fontSize: '13px',
    color: '#374151',
    fontFamily: FONT_FAMILY,
    marginBottom: '2px',
  },
  deliveryTime: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
  },
  shippingNote: {
    fontSize: '11px',
    color: '#9ca3af',
    fontFamily: FONT_FAMILY,
  },
  
  // Options Card
  optionsCard: {
    background: '#fff',
    borderRadius: '16px',
    padding: '20px',
    marginBottom: '20px',
    border: '1px solid #e5e7eb',
  },
  optionGroup: {
    marginBottom: '16px',
  },
  optionLabel: {
    display: 'block',
    fontSize: '12px',
    fontWeight: '700',
    color: '#374151',
    fontFamily: FONT_FAMILY,
    marginBottom: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  pillRow: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  optionPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: FONT_FAMILY,
    borderRadius: '50px',
    border: '2px solid',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none',
  },
  colorDot: {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  sizePill: {
    minWidth: '48px',
    justifyContent: 'center',
  },
  
  // Quantity Dropdown - Compact
  qtySelect: {
    padding: '8px 28px 8px 12px',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: FONT_FAMILY,
    border: '2px solid #e5e7eb',
    borderRadius: '10px',
    background: '#fff',
    color: '#374151',
    cursor: 'pointer',
    outline: 'none',
    width: '70px', // Fixed narrow width
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 10px center',
    backgroundSize: '16px',
  },
  
  // Portrait Prompt - With fun description
  portraitPrompt: {
    background: 'linear-gradient(135deg, #fef3c7 0%, #fef9c3 100%)',
    borderRadius: '20px',
    padding: '24px',
    marginBottom: '16px',
    border: '2px solid rgba(251, 191, 36, 0.3)',
  },
  promptHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    marginBottom: '16px',
  },
  promptIconCircle: {
    width: '52px',
    height: '52px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: 'floatPaw 2s ease-in-out infinite',
    boxShadow: '0 4px 12px rgba(251, 191, 36, 0.4)',
  },
  promptIcon: { fontSize: '26px' },
  promptTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#92400e',
    fontFamily: FONT_FAMILY,
    margin: 0,
  },
  promptSubtitle: {
    fontSize: '14px',
    color: '#b45309',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginTop: '2px',
  },
  promptDescription: {
    background: 'rgba(255, 255, 255, 0.6)',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '16px',
  },
  promptText: {
    fontSize: '14px',
    color: '#78350f',
    fontFamily: FONT_FAMILY,
    lineHeight: 1.6,
    margin: 0,
    marginBottom: '12px',
  },
  promptSteps: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  promptStep: {
    background: '#fff',
    padding: '6px 12px',
    borderRadius: '50px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#92400e',
    fontFamily: FONT_FAMILY,
  },
  promptArrow: {
    color: '#d97706',
    fontSize: '14px',
  },
  createPortraitBtn: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '14px',
    fontSize: '16px',
    fontWeight: '700',
    fontFamily: FONT_FAMILY,
    background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
    border: 'none',
    borderRadius: '14px',
    color: '#fff',
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(147, 51, 234, 0.4)',
  },
  promptNote: {
    textAlign: 'center',
    fontSize: '13px',
    color: '#92400e',
    fontFamily: FONT_FAMILY,
    marginTop: '12px',
    marginBottom: 0,
  },
  
  // Portrait Ready
  portraitReady: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px',
    background: 'linear-gradient(135deg, #dcfce7 0%, #d1fae5 100%)',
    borderRadius: '14px',
    border: '2px solid rgba(34, 197, 94, 0.3)',
  },
  readyThumb: {
    width: '52px',
    height: '52px',
    borderRadius: '12px',
    objectFit: 'cover',
    border: '2px solid #22c55e',
  },
  readyInfo: { flex: 1 },
  readyCheck: {
    display: 'block',
    fontSize: '12px',
    fontWeight: '700',
    color: '#16a34a',
    fontFamily: FONT_FAMILY,
  },
  readyName: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#166534',
    fontFamily: FONT_FAMILY,
  },
  changeLink: {
    padding: '8px 14px',
    fontSize: '13px',
    fontWeight: '600',
    fontFamily: FONT_FAMILY,
    background: '#fff',
    border: '2px solid #22c55e',
    borderRadius: '8px',
    color: '#16a34a',
    cursor: 'pointer',
  },
  // Portrait ready section wrapper
  portraitReadySection: {
    marginBottom: '16px',
  },
  // See on all products CTA
  seeAllProductsBtn: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px 20px',
    marginTop: '10px',
    background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
    border: '2px solid rgba(147, 51, 234, 0.2)',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 12px rgba(147, 51, 234, 0.15)',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: FONT_FAMILY,
    color: '#7c3aed',
  },
  seeAllIcon: {
    fontSize: '16px',
  },
  seeAllArrow: {
    fontSize: '14px',
    color: '#7c3aed',
    marginLeft: '4px',
  },
  
  // Add to Cart
  addToCartBtn: {
    width: '100%',
    padding: '16px 24px',
    fontSize: '17px',
    fontWeight: '700',
    fontFamily: FONT_FAMILY,
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    border: 'none',
    borderRadius: '14px',
    color: '#fff',
    cursor: 'pointer',
    boxShadow: '0 6px 24px rgba(34, 197, 94, 0.5)',
    marginBottom: '8px',
  },
  cartHint: {
    textAlign: 'center',
    fontSize: '13px',
    color: '#9ca3af',
    fontFamily: FONT_FAMILY,
    margin: 0,
  },
  trustSignals: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '16px',
    marginTop: '20px',
    paddingTop: '20px',
    borderTop: '1px solid #e5e7eb',
  },
  trustItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  trustIcon: {
    fontSize: '16px',
    color: '#22c55e',
  },
  trustText: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#6b7280',
    fontFamily: FONT_FAMILY,
  },
  
  // Fun Tabs Section
  tabsSection: {
    marginTop: '48px',
  },
  tabBarWrapper: {
    display: 'flex',
    justifyContent: 'center',
  },
  tabBar: {
    display: 'inline-flex',
    background: '#f3f4f6',
    borderRadius: '12px',
    padding: '4px',
    gap: '4px',
  },
  tabBtn: {
    padding: '12px 24px',
    fontSize: '15px',
    fontWeight: '600',
    fontFamily: FONT_FAMILY,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#6b7280',
    borderRadius: '10px',
    transition: 'all 0.2s ease',
  },
  tabBtnActive: {
    background: '#fff',
    color: '#7c3aed',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  tabPanel: {
    background: '#fff',
    borderRadius: '16px',
    padding: '32px',
    minHeight: '220px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
    border: '1px solid #e5e7eb',
    marginTop: '16px',
  },
  tabsHeaderUnused: {
    display: 'none', // Removed - keeping for reference
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    marginBottom: '24px',
  },
  tabsEmoji: {
    fontSize: '32px',
  },
  tabsTitle: {
    fontSize: '26px',
    fontWeight: '800',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
    margin: 0,
    letterSpacing: '-0.5px',
  },
  // Old tab styles removed
  
  // Vibe Tab
  vibeContent: {},
  vibeCard: {
    background: '#fff',
    borderRadius: '20px',
    padding: '32px',
    textAlign: 'center',
    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
  },
  vibeEmoji: {
    fontSize: '48px',
    display: 'block',
    marginBottom: '16px',
  },
  vibeTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: '16px',
  },
  vibeText: {
    fontSize: '16px',
    color: '#4b5563',
    fontFamily: FONT_FAMILY,
    lineHeight: 1.7,
    margin: 0,
    marginBottom: '24px',
    maxWidth: '500px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  funFactBox: {
    background: 'linear-gradient(135deg, #fef3c7 0%, #fef9c3 100%)',
    borderRadius: '16px',
    padding: '20px',
    display: 'inline-flex',
    alignItems: 'flex-start',
    gap: '12px',
    maxWidth: '400px',
  },
  funFactEmoji: {
    fontSize: '24px',
    flexShrink: 0,
  },
  funFactText: {
    fontSize: '14px',
    color: '#92400e',
    fontFamily: FONT_FAMILY,
    lineHeight: 1.5,
    margin: 0,
    textAlign: 'left',
  },
  
  // Details Tab
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)', // 4 side by side
    gap: '12px',
  },
  detailCard: {
    background: 'linear-gradient(145deg, #faf5ff 0%, #f3e8ff 100%)',
    borderRadius: '16px',
    padding: '16px 12px',
    textAlign: 'center',
    border: '2px solid #e9d5ff',
    transition: 'all 0.2s ease',
    position: 'relative',
    overflow: 'hidden',
  },
  detailEmoji: {
    fontSize: '28px',
    display: 'block',
    marginBottom: '8px',
    animation: 'float 3s ease-in-out infinite',
  },
  detailTitle: {
    fontSize: '10px',
    fontWeight: '700',
    color: '#7c3aed',
    fontFamily: FONT_FAMILY,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    letterSpacing: '0.5px',
    margin: 0,
    marginBottom: '8px',
  },
  detailValue: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: '4px',
    marginBottom: '8px',
  },
  detailDesc: {
    fontSize: '11px',
    color: '#9ca3af',
    fontFamily: FONT_FAMILY,
    lineHeight: 1.4,
    margin: 0,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  
  // Shipping Tab
  shippingContent: {},
  shippingCard: {
    background: '#fff',
    borderRadius: '20px',
    padding: '32px',
    textAlign: 'center',
    maxWidth: '500px',
    margin: '0 auto',
    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
  },
  shippingIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  shippingTitle: {
    fontSize: '26px',
    fontWeight: '700',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: '8px',
  },
  shippingSubtitle: {
    fontSize: '15px',
    color: '#6b7280',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: '24px',
  },
  shippingTable: {
    background: '#f9fafb',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '16px',
  },
  shippingRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 0',
    borderBottom: '1px solid #e5e7eb',
    fontSize: '14px',
    color: '#4b5563',
    fontFamily: FONT_FAMILY,
  },
  shippingValue: {
    fontWeight: '600',
    color: '#1f2937',
  },
  
  // Error
  errorState: {
    textAlign: 'center',
    padding: '80px 20px',
  },
  errorIcon: {
    fontSize: '64px',
    display: 'block',
    marginBottom: '16px',
  },
  errorText: {
    fontSize: '20px',
    color: '#374151',
    fontFamily: FONT_FAMILY,
    marginBottom: '20px',
  },
  backButtonError: {
    padding: '12px 24px',
    fontSize: '15px',
    fontWeight: '600',
    fontFamily: FONT_FAMILY,
    background: '#fff',
    border: '2px solid #9333ea',
    borderRadius: '10px',
    color: '#9333ea',
    cursor: 'pointer',
  },
};

// Responsive
if (typeof window !== 'undefined') {
  const mq = window.matchMedia('(max-width: 900px)');
  if (mq.matches) {
    styles.productLayout.gridTemplateColumns = '1fr';
    styles.productLayout.gap = '32px';
    styles.priceDeliveryRow.gridTemplateColumns = '1fr';
    styles.detailsGrid.gridTemplateColumns = 'repeat(2, 1fr)'; // 2 columns on mobile
    styles.headerBar.marginLeft = '0';
  }
}

export default ProductDetail;
